// Ultimate Card Game (UCG) / VOID Conflict - Tactical Dare Deck Bitfield Encoder
// Encodes and decodes complete 40-card decks into ultra-compact, URL-safe strings (~19 characters).
// Each card in the 41-card canonical roster takes exactly 2 bits (0..3 copies).

import { CARDS_DATA } from '../data/cards.js';
import { DECKS_DATA } from '../data/decks.js';

let _cachedCanonicalNames = null;

export function getCanonicalCardNames() {
  if (!_cachedCanonicalNames) {
    _cachedCanonicalNames = CARDS_DATA
      .filter(c => c && c.name && c.enabled !== false && !c.isCampaignOnly)
      .map(c => c.name)
      .sort();
  }
  return _cachedCanonicalNames;
}

function toBase64Url(uint8Array) {
  if (typeof Buffer !== 'undefined') {
    return Buffer.from(uint8Array).toString('base64url');
  }
  let binary = '';
  for (let i = 0; i < uint8Array.length; i++) {
    binary += String.fromCharCode(uint8Array[i]);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function fromBase64Url(str) {
  if (typeof Buffer !== 'undefined') {
    return new Uint8Array(Buffer.from(str, 'base64url'));
  }
  let base64 = str.replace(/-/g, '+').replace(/_/g, '/');
  while (base64.length % 4) base64 += '=';
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/**
 * Encodes a deck (preset ID, card names list, or custom deck object) into a compact string.
 * - Preset decks return 'p:<id>' (e.g. 'p:Human')
 * - Custom decks return 'c:1.<base64url>' (~19 characters)
 *
 * @param {string|Array|Object} deckInput
 * @returns {string} Compact deck code
 */
export function encodeDeck(deckInput) {
  if (!deckInput) return 'p:Human';

  // Check if it's a known preset ID
  if (typeof deckInput === 'string' && DECKS_DATA[deckInput]) {
    return `p:${deckInput}`;
  }

  // Extract card names from input
  let cardNames = [];
  if (Array.isArray(deckInput)) {
    cardNames = deckInput.map(item => (typeof item === 'string' ? item : item?.name || '')).filter(Boolean);
  } else if (typeof deckInput === 'object') {
    if (deckInput.cards && typeof deckInput.cards === 'object') {
      for (const [name, qty] of Object.entries(deckInput.cards)) {
        const count = Math.min(3, Math.max(0, Number(qty) || 0));
        for (let i = 0; i < count; i++) cardNames.push(name);
      }
    } else {
      const all = [...(deckInput.common || []), ...(deckInput.uncommon || []), ...(deckInput.epic || [])];
      cardNames = all.filter(Boolean);
    }
  }

  if (cardNames.length === 0) {
    return 'p:Human';
  }

  // Count occurrences of each card
  const counts = {};
  for (const name of cardNames) {
    counts[name] = (counts[name] || 0) + 1;
  }

  const canonical = getCanonicalCardNames();
  const byteLength = Math.ceil(canonical.length / 4);
  const bytes = new Uint8Array(byteLength);

  for (let i = 0; i < canonical.length; i++) {
    const name = canonical[i];
    const count = Math.min(3, Math.max(0, counts[name] || 0));
    const byteIndex = Math.floor(i / 4);
    const bitShift = (i % 4) * 2;
    bytes[byteIndex] |= (count << bitShift);
  }

  return `c:1.${toBase64Url(bytes)}`;
}

/**
 * Decodes a deck code ('p:<id>' or 'c:1.<base64url>') into either a preset ID or an array of card names.
 *
 * @param {string} deckCode
 * @returns {string|Array<string>} Preset ID string or Array of card names
 */
export function decodeDeck(deckCode) {
  if (!deckCode || typeof deckCode !== 'string') {
    return 'Human';
  }

  const clean = deckCode.trim();

  // Preset check
  if (clean.startsWith('p:')) {
    const presetId = clean.slice(2);
    if (DECKS_DATA[presetId]) return presetId;
    // Case-insensitive match fallback
    for (const key of Object.keys(DECKS_DATA)) {
      if (key.toLowerCase() === presetId.toLowerCase()) return key;
    }
    return 'Human';
  }

  // Custom bitfield check
  if (clean.startsWith('c:1.')) {
    try {
      const rawBase64 = clean.slice(4);
      if (!rawBase64 || !/^[A-Za-z0-9_-]+$/.test(rawBase64)) {
        return 'Human';
      }
      const bytes = fromBase64Url(rawBase64);
      const canonical = getCanonicalCardNames();
      const expectedByteLen = Math.ceil(canonical.length / 4);
      if (bytes.length !== expectedByteLen) {
        return 'Human';
      }
      const restored = [];

      for (let i = 0; i < canonical.length; i++) {
        const byteIndex = Math.floor(i / 4);
        if (byteIndex >= bytes.length) break;
        const bitShift = (i % 4) * 2;
        const count = (bytes[byteIndex] >> bitShift) & 0b11;
        for (let c = 0; c < count; c++) {
          restored.push(canonical[i]);
        }
      }

      if (restored.length > 0) {
        return restored;
      }
    } catch (err) {
      console.warn('[deckEncoder] Failed to decode custom deck bitfield:', err);
    }
  }

  return 'Human';
}
